import { flushChatOutbox } from "@/domains/offline/chat/chatSyncEngine";
import { getConnectivityOnline } from "@/domains/offline/connectivity/connectivityStore";
import { clearOfflineData } from "@/domains/offline/db/mivoDb";
import { flushOutbox } from "@/domains/offline/sync/syncEngine";
import { authApi } from "@/domains/shared/apis/authApi";
import { api } from "@/domains/shared/services/api";
import { queryClient } from "@/domains/shared/services/queryClient";
import { resetCsrfToken } from "@/domains/shared/services/setupCsrfInterceptor";
import { assignLocation } from "@/domains/shared/utils/browserLocation";

type SeenDeployVersion = {
    version: string;
    offlineSchema: number;
    seenAt: number;
};

type DeployEnforcementRecord = {
    targetVersion: string;
    offlineSchema: number;
    enforcedAt: number;
    message: string;
};

const SEEN_DEPLOY_VERSION_STORAGE_KEY = "mivo_seen_deploy_version";
const DEPLOY_ENFORCEMENT_STORAGE_KEY = "mivo_deploy_enforcement";
export const DEPLOY_NOTICE_STORAGE_KEY = "mivo_deploy_notice";
const LAST_HANDLED_DEPLOY_VERSION_STORAGE_KEY = "mivo_last_handled_deploy_version";

export const DEPLOY_DEFAULT_MESSAGE =
    "Sua sessão foi encerrada após uma atualização do sistema. Faça login novamente.";

export const DEPLOY_SYNC_TIMEOUT_MS = 10_000;
export const DEPLOY_ENFORCEMENT_COOLDOWN_MS = 5 * 60_000;

let handling = false;

function readJson<T>(storage: Storage | undefined, key: string): T | null {
    if (!storage) {
        return null;
    }
    const raw = storage.getItem(key);

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as T;
    } catch {
        storage.removeItem(key);
        return null;
    }
}

function writeJson<T>(storage: Storage | undefined, key: string, value: T): void {
    if (!storage) {
        return;
    }
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore quota / disabled storage write errors
    }
}

function readSeenDeployVersion(): SeenDeployVersion | null {
    return readJson<SeenDeployVersion>(typeof sessionStorage !== "undefined" ? sessionStorage : undefined, SEEN_DEPLOY_VERSION_STORAGE_KEY);
}

function writeSeenDeployVersion(version: string, offlineSchema: number): void {
    writeJson<SeenDeployVersion>(typeof sessionStorage !== "undefined" ? sessionStorage : undefined, SEEN_DEPLOY_VERSION_STORAGE_KEY, {
        version,
        offlineSchema,
        seenAt: Date.now(),
    });
}

function readDeployEnforcementRecord(): DeployEnforcementRecord | null {
    return readJson<DeployEnforcementRecord>(typeof localStorage !== "undefined" ? localStorage : undefined, DEPLOY_ENFORCEMENT_STORAGE_KEY);
}

function writeDeployEnforcementRecord(record: DeployEnforcementRecord): void {
    writeJson<DeployEnforcementRecord>(typeof localStorage !== "undefined" ? localStorage : undefined, DEPLOY_ENFORCEMENT_STORAGE_KEY, record);
}

function markDeployNotice(message: string): void {
    if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(DEPLOY_NOTICE_STORAGE_KEY, message);
    }
}

function markLastHandledDeployVersion(targetVersion: string): void {
    writeJson(typeof sessionStorage !== "undefined" ? sessionStorage : undefined, LAST_HANDLED_DEPLOY_VERSION_STORAGE_KEY, targetVersion);
}

function wasDeployVersionHandled(targetVersion: string): boolean {
    return sessionStorage.getItem(LAST_HANDLED_DEPLOY_VERSION_STORAGE_KEY) === targetVersion;
}

function wasRecentlyEnforced(targetVersion: string): boolean {
    const record = readDeployEnforcementRecord();

    if (!record) {
        return false;
    }

    return record.targetVersion === targetVersion && Date.now() - record.enforcedAt < DEPLOY_ENFORCEMENT_COOLDOWN_MS;
}

async function syncOfflineStateWithinTimeout(): Promise<void> {
    if (!getConnectivityOnline()) {
        return;
    }

    await Promise.race([
        (async () => {
            await flushOutbox(api);
            await flushChatOutbox();
        })(),
        new Promise<void>((resolve) => {
            window.setTimeout(resolve, DEPLOY_SYNC_TIMEOUT_MS);
        }),
    ]);
}

async function runDeployEnforcement(
    targetVersion: string,
    offlineSchema: number,
    shouldSync: boolean,
    message: string,
    shouldBroadcast: boolean,
): Promise<void> {
    if (handling) {
        return;
    }

    handling = true;

    try {
        writeSeenDeployVersion(targetVersion, offlineSchema);
        markLastHandledDeployVersion(targetVersion);
        markDeployNotice(message);

        if (shouldBroadcast) {
            writeDeployEnforcementRecord({
                targetVersion,
                offlineSchema,
                enforcedAt: Date.now(),
                message,
            });
        }

        if (shouldSync) {
            try {
                await syncOfflineStateWithinTimeout();
            } catch {
                /* deploy logout must continue even when sync fails */
            }
        }

        resetCsrfToken();
        await queryClient.cancelQueries();
        queryClient.clear();
        await clearOfflineData();

        if (getConnectivityOnline()) {
            try {
                await authApi.logout();
            } catch {
                /* ignore — local reset and reload remain authoritative */
            }
        }

        assignLocation("/login");
    } finally {
        handling = false;
    }
}

export function clearSeenDeployVersion(): void {
    sessionStorage.removeItem(SEEN_DEPLOY_VERSION_STORAGE_KEY);
    sessionStorage.removeItem(LAST_HANDLED_DEPLOY_VERSION_STORAGE_KEY);
}

export function consumeDeployNotice(): string | null {
    const message = sessionStorage.getItem(DEPLOY_NOTICE_STORAGE_KEY);

    if (message) {
        sessionStorage.removeItem(DEPLOY_NOTICE_STORAGE_KEY);
    }

    return message;
}

export function isDeployEnforcementHandling(): boolean {
    return handling;
}

export async function observeDeployVersion(version: string, offlineSchema: number): Promise<boolean> {
    const normalizedVersion = version.trim();

    if (!normalizedVersion || !Number.isInteger(offlineSchema) || offlineSchema < 0) {
        return false;
    }

    const seen = readSeenDeployVersion();

    if (!seen) {
        writeSeenDeployVersion(normalizedVersion, offlineSchema);
        return false;
    }

    if (seen.version === normalizedVersion) {
        if (seen.offlineSchema !== offlineSchema) {
            writeSeenDeployVersion(normalizedVersion, offlineSchema);
        }

        return false;
    }

    if (wasRecentlyEnforced(normalizedVersion)) {
        writeSeenDeployVersion(normalizedVersion, offlineSchema);
        markLastHandledDeployVersion(normalizedVersion);
        return false;
    }

    await runDeployEnforcement(
        normalizedVersion,
        offlineSchema,
        seen.offlineSchema === offlineSchema,
        DEPLOY_DEFAULT_MESSAGE,
        true,
    );

    return true;
}

export function installDeployVersionListener(): () => void {
    const onStorage = (event: StorageEvent) => {
        if (event.key !== DEPLOY_ENFORCEMENT_STORAGE_KEY || !event.newValue) {
            return;
        }

        let record: DeployEnforcementRecord | null = null;

        try {
            record = JSON.parse(event.newValue) as DeployEnforcementRecord;
        } catch {
            return;
        }

        if (!record || wasDeployVersionHandled(record.targetVersion)) {
            return;
        }

        void runDeployEnforcement(
            record.targetVersion,
            record.offlineSchema,
            false,
            record.message || DEPLOY_DEFAULT_MESSAGE,
            false,
        );
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
}
